import { useEffect, useRef, type ReactElement, type ReactNode } from "react";
import { up } from "../lib/tr";

export interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

/** Accessible modal: labelled, Escape closes, focus moves in and returns to the opener, Tab stays inside. */
export function Dialog({ open, title, onClose, children }: DialogProps): ReactElement | null {
  const ref = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    const box = ref.current;
    const focusables = () =>
      box ? [...box.querySelectorAll<HTMLElement>("button, [href], textarea, input, select, [tabindex]:not([tabindex='-1'])")] : [];
    (focusables()[0] ?? box)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "Tab") {
        const list = focusables();
        const first = list[0];
        const last = list.at(-1);
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener.current instanceof HTMLElement) opener.current.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="z-dialog-backdrop" onClick={onClose}>
      <div
        ref={ref}
        className="z-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="z-dialog-title"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="z-tape z-tape--tl" aria-hidden="true" />
        <h2 id="z-dialog-title" className="z-dialog__title">
          {up(title)}
        </h2>
        {children}
        <button type="button" className="z-btn z-btn--ghost z-dialog__close" onClick={onClose}>
          {up("vazgeç")}
        </button>
      </div>
    </div>
  );
}
